import { useImperativeHandle, forwardRef, useRef, useEffect } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'

export type CanvasCaptureRef = {
  captureCanvas: () => Promise<string>
  captureOffscreen: (
    cameraPosition: THREE.Vector3,
    cameraTarget: THREE.Vector3,
    elementId?: string,
    elementPosition?: THREE.Vector3,
  ) => Promise<string>
}

const CanvasCapture = forwardRef<CanvasCaptureRef>((props, ref) => {
  const { gl, scene, camera } = useThree()
  const offscreenRendererRef = useRef<THREE.WebGLRenderer | null>(null)
  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    return () => {
      if (offscreenRendererRef.current) {
        offscreenRendererRef.current.dispose()
        offscreenRendererRef.current = null
      }
      if (offscreenCanvasRef.current) {
        offscreenCanvasRef.current = null
      }
    }
  }, [])

  const captureCanvas = async (): Promise<string> => {
    return new Promise((resolve) => {
      gl.render(scene, camera)

      const canvas = gl.domElement

      const captureCanvas = document.createElement('canvas')
      const ctx = captureCanvas.getContext('2d')

      if (!ctx) {
        throw new Error('Could not get 2D context')
      }

      captureCanvas.width = canvas.width
      captureCanvas.height = canvas.height

      ctx.fillStyle = '#f0f0f0'
      ctx.fillRect(0, 0, captureCanvas.width, captureCanvas.height)

      ctx.drawImage(canvas, 0, 0)

      const htmlOverlays = document.querySelectorAll('[data-element-id]')

      console.log('Found', htmlOverlays.length, 'element ID overlays')

      if (htmlOverlays.length > 0) {
        htmlOverlays.forEach((overlay) => {
          const htmlElement = overlay as HTMLElement

          if (
            htmlElement.offsetWidth === 0 ||
            htmlElement.offsetHeight === 0 ||
            window.getComputedStyle(htmlElement).display === 'none'
          ) {
            console.log('Element not visible:', htmlElement)
            return
          }

          const rect = htmlElement.getBoundingClientRect()
          const canvasRect = canvas.getBoundingClientRect()

          const x = rect.left - canvasRect.left + rect.width / 2
          const y = rect.top - canvasRect.top + rect.height / 2

          const textContent =
            htmlElement.getAttribute('data-element-id') ||
            htmlElement.textContent ||
            htmlElement.innerText

          console.log('Capturing element ID:', textContent, 'at position:', { x, y })

          if (!textContent) return

          const computedStyle = window.getComputedStyle(htmlElement)

          ctx.save()

          // Set font
          const fontSize = parseInt(computedStyle.fontSize) || 12
          ctx.font = `${fontSize}px ${computedStyle.fontFamily || 'sans-serif'}`

          // Get border color
          const borderColor = computedStyle.borderColor || computedStyle.color || '#000'

          const padding = 6
          const metrics = ctx.measureText(textContent)
          const textWidth = metrics.width
          const textHeight = fontSize * 1.2

          ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
          ctx.fillRect(
            x - textWidth / 2 - padding,
            y - textHeight / 2 - padding,
            textWidth + padding * 2,
            textHeight + padding * 2,
          )

          // Draw border
          ctx.strokeStyle = borderColor
          ctx.lineWidth = 2
          ctx.strokeRect(
            x - textWidth / 2 - padding,
            y - textHeight / 2 - padding,
            textWidth + padding * 2,
            textHeight + padding * 2,
          )

          ctx.fillStyle = computedStyle.color || '#000'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(textContent, x, y)

          ctx.restore()
        })
      } else {
        console.warn('No element IDs found in the DOM')
      }

      // Optimize/Resize
      const MAX_WIDTH = 1024
      let finalCanvas = captureCanvas
      if (captureCanvas.width > MAX_WIDTH) {
        const scale = MAX_WIDTH / captureCanvas.width
        const resizeCanvas = document.createElement('canvas')
        resizeCanvas.width = MAX_WIDTH
        resizeCanvas.height = captureCanvas.height * scale
        const resizeCtx = resizeCanvas.getContext('2d')
        if (resizeCtx) {
          resizeCtx.drawImage(captureCanvas, 0, 0, resizeCanvas.width, resizeCanvas.height)
          finalCanvas = resizeCanvas
        }
      }

      const dataURL = finalCanvas.toDataURL('image/jpeg', 0.9)
      resolve(dataURL)
    })
  }

  const captureOffscreen = async (
    cameraPosition: THREE.Vector3,
    cameraTarget: THREE.Vector3,
    elementId?: string,
    elementPosition?: THREE.Vector3,
  ): Promise<string> => {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        const width = 800
        const height = 600

        if (!offscreenCanvasRef.current) {
          offscreenCanvasRef.current = document.createElement('canvas')
          offscreenCanvasRef.current.width = width
          offscreenCanvasRef.current.height = height
        }

        if (!offscreenRendererRef.current) {
          offscreenRendererRef.current = new THREE.WebGLRenderer({
            canvas: offscreenCanvasRef.current,
            antialias: true,
            alpha: false,
            preserveDrawingBuffer: true,
          })
          offscreenRendererRef.current.setSize(width, height)
          offscreenRendererRef.current.setClearColor(0xf0f0f0, 1)
        }

        const offscreenCanvas = offscreenCanvasRef.current
        const offscreenRenderer = offscreenRendererRef.current

        const offscreenCamera = new THREE.PerspectiveCamera(40, width / height, 0.1, 1000)
        offscreenCamera.position.copy(cameraPosition)
        offscreenCamera.lookAt(cameraTarget)
        offscreenCamera.updateProjectionMatrix()

        offscreenRenderer.render(scene, offscreenCamera)

        gl.render(scene, camera)

        const ctx = offscreenCanvas.getContext('2d')
        if (ctx && elementId && elementPosition) {
          ctx.save()

          const worldPos = elementPosition.clone()
          const screenPos = worldPos.project(offscreenCamera)
          const x = ((screenPos.x + 1) / 2) * width
          const y = ((-screenPos.y + 1) / 2) * height

          if (screenPos.z < 1 && screenPos.z > -1) {
            const ringRadius = 40
            const ringWidth = 3

            ctx.shadowColor = 'rgba(0, 0, 0, 0.4)'
            ctx.shadowBlur = 8
            ctx.shadowOffsetX = 0
            ctx.shadowOffsetY = 0

            ctx.strokeStyle = '#FFD700'
            ctx.lineWidth = ringWidth
            ctx.beginPath()
            ctx.arc(x, y, ringRadius, 0, Math.PI * 2)
            ctx.stroke()

            ctx.shadowColor = 'rgba(255, 215, 0, 0.3)'
            ctx.shadowBlur = 12
            ctx.beginPath()
            ctx.arc(x, y, ringRadius + 5, 0, Math.PI * 2)
            ctx.stroke()

            ctx.shadowColor = 'transparent'
            ctx.shadowBlur = 0

            const fontSize = 24
            ctx.font = `bold ${fontSize}px sans-serif`
            ctx.fillStyle = '#FFD700'
            ctx.strokeStyle = '#000'
            ctx.lineWidth = 2
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'

            ctx.strokeText(elementId, x, y)
            ctx.fillText(elementId, x, y)
          }

          ctx.restore()
        }

        const dataURL = offscreenCanvas.toDataURL('image/jpeg', 0.9)
        resolve(dataURL)
      })
    })
  }

  useImperativeHandle(ref, () => ({
    captureCanvas,
    captureOffscreen,
  }))

  return null
})

export default CanvasCapture
